import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { V1SectionContainer } from './V1SectionContainer';

interface Attachment {
  id: string;
  name?: string;
  title?: string;
  description?: string;
  fileUrl?: string;
  imageThumbUrl?: string;
}

interface Section {
  id: string;
  typeOf: string;
  customTitle?: string;
  attachments?: Attachment[];
  sectionOption?: {
    displayStyle?: string;
    [key: string]: any;
  };
}

interface V1PhotosSectionProps {
  section: Section;
}

/**
 * V1 Photos Section - FROZEN
 *
 * Displays photo galleries with grid layout and lightbox.
 * Simplified version without carousel or advanced features.
 *
 * DO NOT add V2 features to this component.
 */
export const V1PhotosSection: React.FC<V1PhotosSectionProps> = ({ section }) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);

  // Convert attachments to photos array
  const photos = section.attachments?.map((attachment, index) => ({
    id: attachment.id || `photo-${index}`,
    url: attachment.fileUrl || '',
    caption: attachment.name || attachment.title || attachment.description,
    thumbnailUrl: attachment.imageThumbUrl || attachment.fileUrl || ''
  })) || [];

  const openLightbox = (index: number) => {
    setSelectedPhotoIndex(index);
  };

  const closeLightbox = () => {
    setSelectedPhotoIndex(null);
  };

  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (selectedPhotoIndex === null) return;

    const newIndex = direction === 'prev'
      ? (selectedPhotoIndex - 1 + photos.length) % photos.length
      : (selectedPhotoIndex + 1) % photos.length;

    setSelectedPhotoIndex(newIndex);
  };

  // Grid Layout (V1 default)
  const renderGrid = () => {
    return (
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            className="relative group cursor-pointer overflow-hidden rounded-lg aspect-square bg-gray-100"
            onClick={() => openLightbox(index)}
          >
            <img
              src={photo.thumbnailUrl}
              alt={photo.caption || `Photo ${index + 1}`}
              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
            />
            {photo.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <p className="text-white text-sm">{photo.caption}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Lightbox Component
  const renderLightbox = () => {
    if (selectedPhotoIndex === null || !photos[selectedPhotoIndex]) return null;

    const selectedPhoto = photos[selectedPhotoIndex];

    return (
      <div
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
        onClick={closeLightbox}
      >
        <button
          onClick={closeLightbox}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
          aria-label="Close lightbox"
        >
          <X className="w-8 h-8" />
        </button>

        {photos.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateLightbox('prev');
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
              aria-label="Previous photo"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                navigateLightbox('next');
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
              aria-label="Next photo"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          </>
        )}

        <div
          className="max-w-7xl max-h-[90vh] mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={selectedPhoto.url}
            alt={selectedPhoto.caption || `Photo ${selectedPhotoIndex + 1}`}
            className="max-w-full max-h-[80vh] object-contain"
          />
          {selectedPhoto.caption && (
            <p className="text-white text-center mt-4">{selectedPhoto.caption}</p>
          )}
          <p className="text-white/60 text-center mt-2">
            {selectedPhotoIndex + 1} / {photos.length}
          </p>
        </div>
      </div>
    );
  };

  if (photos.length === 0) {
    return (
      <V1SectionContainer section={section}>
        {section.customTitle && (
          <h2 className="text-2xl font-bold mb-4">{section.customTitle}</h2>
        )}
        <p className="text-gray-500">No photos available.</p>
      </V1SectionContainer>
    );
  }

  return (
    <>
      <V1SectionContainer section={section}>
        {section.customTitle && (
          <h2 className="text-2xl font-bold mb-6">{section.customTitle}</h2>
        )}
        {renderGrid()}
      </V1SectionContainer>
      {renderLightbox()}
    </>
  );
};